import React, { useState } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { Delete, MoreOptionsIcon, ReinviteIcon, UserIcon } from "components/atoms/Icon";
import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";
import { usePermissions } from "hooks/usePermissions";
import { UserPermissions } from "utils/enums";
import { useSetRoles } from "mutations/Roles/useSetRolesMutation";
import { useAddUsers } from "mutations/Users/useAddUsers";
import { useRemoveUsers } from "mutations/Users/useRemoveUsers";

import DeleteUserModal from "./DeleteUserModal";
import styles from "./UserManagementMenu.module.scss";

interface UserManagementMenuProps {
    userId: number;
    email: string;
}

function UserManagementMenu({ userId, email }: UserManagementMenuProps) {
    const { hasPermission, isFetching } = usePermissions();
    const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const { mutate: mutateSetRoles, isLoading: setRolesIsLoading } = useSetRoles();
    const { mutate: mutateRemoveUser, isLoading: removeUserIsLoading } = useRemoveUsers();
    const { mutate: mutateAddUsers } = useAddUsers();

    if (isFetching) {
        return <LoadingSpinner />;
    }

    const handleCancel = () => {
        setModalIsOpen(false);
    };

    const handleMoreOptionsClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElement(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorElement(null);
    };

    const handleMakeMember = () => {
        mutateSetRoles({ userId, roles: ["Member"] });
        handleClose();
    };

    const handleMakeAdmin = () => {
        mutateSetRoles({ userId, roles: ["Admin"] });
        handleClose();
    };

    const handleMakeAudetteAdmin = () => {
        mutateSetRoles({ userId, roles: ["Audette Admin"] });
        handleClose();
    };

    const handleRemoveUser = () => {
        mutateRemoveUser({ userId });
        handleClose();
    };

    const handleReinvite = () => {
        mutateRemoveUser(
            {
                userId,
                doNotInvalidate: true,
            },
            {
                onSuccess: () =>
                    mutateAddUsers({
                        emails: [email],
                        userIsReinvited: true,
                    }),
            }
        );
        handleClose();
    };

    return (
        <>
            <div>
                <IconButton onClick={handleMoreOptionsClick}>
                    <MoreOptionsIcon />
                </IconButton>
                <Menu anchorEl={anchorElement} open={Boolean(anchorElement)} onClose={handleClose}>
                    {hasPermission(UserPermissions.AddUser) && (
                        <MenuItem onClick={() => handleReinvite()}>
                            <div className={styles.buttonContainer}>
                                <ReinviteIcon />
                                Reinvite
                            </div>
                        </MenuItem>
                    )}

                    {hasPermission(UserPermissions.GrantUser) && (
                        <MenuItem onClick={() => handleMakeMember()} disabled={setRolesIsLoading}>
                            <div className={styles.buttonContainer}>
                                <UserIcon />
                                Make Member
                            </div>
                        </MenuItem>
                    )}
                    {hasPermission(UserPermissions.GrantUser) && (
                        <MenuItem onClick={() => handleMakeAdmin()} disabled={setRolesIsLoading}>
                            <div className={styles.buttonContainer}>
                                <UserIcon />
                                Make Admin
                            </div>
                        </MenuItem>
                    )}
                    {hasPermission(UserPermissions.GrantAudetteAdmin) && (
                        <MenuItem onClick={() => handleMakeAudetteAdmin()} disabled={setRolesIsLoading}>
                            <div className={styles.buttonContainer}>
                                <UserIcon />
                                Make Audette Admin
                            </div>
                        </MenuItem>
                    )}

                    {hasPermission(UserPermissions.AddUser) && (
                        <MenuItem
                            onClick={() => {
                                setModalIsOpen(true);
                                handleClose();
                            }}
                            disabled={removeUserIsLoading}
                        >
                            <div className={`${styles.buttonContainer} ${styles.deleteButton}`}>
                                <Delete />
                                Remove
                            </div>
                        </MenuItem>
                    )}
                </Menu>
            </div>
            <DeleteUserModal
                modalIsOpen={modalIsOpen}
                email={email}
                onCancel={handleCancel}
                handleMenuItemClick={handleRemoveUser}
            />
        </>
    );
}

export default UserManagementMenu;
